import { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LangProvider } from './context/LangContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import AdminLayout from './components/AdminLayout';
import DashboardPage from './pages/DashboardPage';
import BlogPage from './pages/BlogPage';
import BlogEditor from './pages/BlogEditor';
import PagesPage from './pages/PagesPage';
import PageEditor from './pages/PageEditor';
import ProductsPage from './pages/ProductsPage';
import ProductsImportPage from './pages/ProductsImportPage';
import ProductEditor from './pages/ProductEditor';
import OrdersPage from './pages/OrdersPage';
import OrderDetail from './pages/OrderDetail';
import ClientsPage from './pages/ClientsPage';
import ClientDetail from './pages/ClientDetail';
import FinancialPage from './pages/FinancialPage';
import ProspectsPage from './pages/ProspectsPage';
import ProspectDetail from './pages/ProspectDetail';
import SettingsPage from './pages/SettingsPage';
import SeoPage from './pages/SeoPage';
import ChatBotPage from './pages/ChatBotPage';
import MediaLibraryPage from './pages/MediaLibraryPage';
import CurieratPage from './pages/CurieratPage';
import UsersPage from './pages/UsersPage';
import HelpPage from './pages/HelpPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { isLoggedIn, login, logout, currentUser } = useAuth();
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());

  useEffect(() => { setLoggedIn(isLoggedIn()); }, [isLoggedIn, currentUser]);

  const handleLogin = (username: string, password: string): boolean => {
    const ok = login(username, password);
    if (ok) setLoggedIn(true);
    return ok;
  };

  const handleLogout = () => { logout(); setLoggedIn(false); };

  return (
    <Routes>
      <Route path="/login" element={
        loggedIn ? <Navigate to="/" replace /> :
        <LoginPage onLogin={handleLogin} />
      } />
      <Route path="/" element={
        <RequireAuth><AdminLayout onLogout={handleLogout} /></RequireAuth>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/new" element={<BlogEditor />} />
        <Route path="blog/:id" element={<BlogEditor />} />
        <Route path="pages" element={<PagesPage />} />
        <Route path="pages/:id" element={<PageEditor />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/import" element={<ProductsImportPage />} />
        <Route path="products/new" element={<ProductEditor />} />
        <Route path="products/:id" element={<ProductEditor />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/new" element={<OrderDetail />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={<ClientDetail />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="financial" element={<FinancialPage />} />
        <Route path="prospects" element={<ProspectsPage />} />
        <Route path="prospects/:id" element={<ProspectDetail />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="seo" element={<SeoPage />} />
        <Route path="curierat" element={<CurieratPage />} />
        <Route path="media" element={<MediaLibraryPage />} />
        <Route path="chatbot" element={<ChatBotPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="help" element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </AuthProvider>
    </LangProvider>
  );
}
